import { FC } from 'react';
import type { RemediationGeneratedContent } from '@libs/types';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Markdown } from '@components/Markdown';

import { sameHeading } from '../utils/same-heading';

import { RemediationFigure } from './RemediationFigure';
import { RemediationTable } from './RemediationTable';
import { SpreadsheetPane } from './SpreadsheetPane';

/**
 * Read-only preview of an AI-written document, so a GENERATE item can be
 * accepted or rejected on its contents rather than on its title.
 *
 * Renders the canonical block model directly instead of going through the PDF
 * the backend produces at finalize — that PDF does not exist yet at review
 * time, which is the whole point of reviewing first.
 */

export const GeneratedDocumentPreview: FC<{
  content: RemediationGeneratedContent;
  /** Needed to fetch the run-scoped figures embedded in this document. */
  runId: string;
  /**
   * The document's name as the surrounding page already shows it. Supplying it
   * suppresses a leading section or heading block that just repeats it.
   *
   * The rendered PDF emits `<h1>{title}</h1>` above the sections, so a section
   * named after the document reads as a subtitle there. In the review pane the
   * page header is already the title, so the same section shows up as the name
   * printed twice.
   */
  documentTitle?: string | null;
}> = observer(({ content, runId, documentTitle }) => {
    // A workbook is rendered as a workbook. Listing header names, which is what
    // this did first, hides both the data and the shape of the change.
    if (content.format === 'spreadsheet') {
      return <SpreadsheetPane content={content} />;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {content.sections.map((section, sectionIndex) => {
          const sectionRepeatsTitle = sameHeading(section.sectionName, documentTitle);
          return (
          <Box
            // sectionKey is null for non-canonical content and can repeat, so it
            // cannot be the key on its own.
            key={`${section.sectionKey ?? 'section'}-${sectionIndex}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
          >
            {/* `sectionName` is a compliance-catalog taxonomy label, not a
                heading the document itself has, so it is styled as UI chrome
                — small, uppercase, grey — rather than as document type. Matches
                the gutter label `ProposedPane` uses for the same field, so the
                GENERATE preview and the REWRITE review pane read as one system. */}
            {!sectionRepeatsTitle && (
              <Box
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.tertiary',
                }}
              >
                {section.sectionName}
              </Box>
            )}
            {section.blocks.map((block, blockIndex) => {
              // A leading heading that just restates the section — or the
              // document — is the AI writing a title page into the body. It
              // reads as the same name printed twice.
              if (
                blockIndex === 0 &&
                block.kind === 'heading' &&
                (sameHeading(block.text, section.sectionName) ||
                  sameHeading(block.text, documentTitle))
              ) {
                return null;
              }
              if (block.kind === 'table') {
                // Table content, not a label about one. `column_addition` is a
                // spreadsheet change-type string — printing it here stood a
                // caption about spreadsheet columns in for the prose table the
                // scaffold actually generated.
                return <RemediationTable key={block.blockId} table={block.table} />;
              }
              if (block.kind === 'diagram') {
                // The image itself. This printed the raw `diagramId` before —
                // an internal identifier standing in for the artwork a reviewer
                // is being asked to approve.
                return (
                  <RemediationFigure
                    key={block.blockId}
                    runId={runId}
                    pngKey={block.diagram?.pngKey}
                    caption={block.text}
                    width={block.diagram?.width}
                    height={block.diagram?.height}
                  />
                );
              }
              return (
                <Typography
                  key={block.blockId}
                  component="div"
                  variant={block.kind === 'heading' ? 'textSM_semiBold' : 'textSM_regular'}
                  sx={{
                    color: block.kind === 'heading' ? 'text.primary' : 'text.secondary',
                    pl: block.kind === 'list_item' ? 2 : 0,
                  }}
                >
                  {block.kind === 'list_item' ? '• ' : ''}
                  {/* Block text is AI-authored markdown, not plain text. */}
                  <Markdown inline>{block.text}</Markdown>
                </Typography>
              );
            })}
          </Box>
          );
        })}
      </Box>
    );
  });
